import { inject, Injectable } from '@angular/core';
import { DeviceCounter } from '@shared/models/device-info';
import { DEVICE_COUNTER_STORE } from './device-counter-store.token';
import { DeviceCounterSyncLogger } from './device-counter-sync-logger.service';
import {
  DeviceCounterEntityType,
  DeviceCounterOperationResult,
  DeviceCounterStateChange
} from './device-counter.models';

/**
 * Application-level device counter operations — the single entry point every
 * consumer (document codes, closure codes, counter re-seeding from the server)
 * uses.
 *
 * Firestore is the source of truth: the store behind `DEVICE_COUNTER_STORE`
 * performs the operation with the Desktop's rules from
 * `device-counter-transitions.ts`, and this facade records the outcome of every
 * write in Seq.
 *
 * `undefined` still means "the counter could not be read or written" — the
 * contract consumers already branch on when they fall back to a code of 1.
 */
@Injectable({ providedIn: 'root' })
export class DeviceCounterService {
  private readonly store = inject(DEVICE_COUNTER_STORE);
  private readonly syncLogger = inject(DeviceCounterSyncLogger);

  /** The register's current counter, `undefined` when it does not exist yet. */
  getAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounter | undefined> {
    return this.store.getAsync(cashRegisterId, entityType);
  }

  async incrementAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterOperationResult> {
    return this.log(await this.store.incrementAsync(cashRegisterId, entityType));
  }

  async resetAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterOperationResult> {
    return this.log(await this.store.resetAsync(cashRegisterId, entityType));
  }

  async setAsync(cashRegisterId: string, entityType: DeviceCounterEntityType, value: number): Promise<DeviceCounterOperationResult> {
    return this.log(await this.store.setAsync(cashRegisterId, entityType, value));
  }

  //#region Seq logging

  /**
   * Record the outcome of a write: the backend acknowledgement logs a success,
   * a refusal logs a replayable failure, and a skipped stale write logs its own
   * reconciliation record. Logging never affects the result the caller gets.
   */
  private log(result: DeviceCounterOperationResult): DeviceCounterOperationResult {
    const change: DeviceCounterStateChange | undefined = result.change;
    if (!change) return result;

    result.firestoreSync.then(outcome => {
      if (outcome.status === 'skipped' && outcome.reason === 'stale') {
        this.syncLogger.logStaleChangeSkipped(change, outcome.existingStamp);
        return;
      }
      if (outcome.status === 'applied') {
        const queuedAt = new Date();
        outcome.acknowledged.then(
          () => this.syncLogger.logSyncSucceeded(change, outcome.created, queuedAt),
          error => this.syncLogger.logSyncFailure(change, error)
        ).catch(() => undefined);
      }
    }).catch(() => undefined);   // a logger that throws must not become an unhandled rejection
    return result;
  }

  //#endregion
}
